import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import { withRouter, RouteComponentProps } from "react-router-dom";

import { BreadCrumb } from "../../../components/Common";
import { ControlFeatureDto } from "../../../interfaces/control-feature";
import { PaginateQuery } from "../../../interfaces";
import {
  useGetFeaturesQuery,
  useDeleteFeatureMutation,
  useToggleFeatureStateMutation,
} from "../../../store/api/featureApi";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditFeatureModal } from "./AddEditFeatureModal";
import { Paginator } from "../../../components/Pagination";

type TProps = RouteComponentProps & {};

const FeaturesPageComponent: React.FC<TProps> = () => {
  const { t, i18n } = useTranslation();
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const initState = new PaginateQuery(10, "sequence_number:ASC");
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);

  const {
    data: response,
    isLoading,
    isSuccess,
  } = useGetFeaturesQuery(paginateQuery);
  const [deleteFeature, { isLoading: deleteLoading }] =
    useDeleteFeatureMutation();

  const [toggleFeatureState] = useToggleFeatureStateMutation();

  const features: ControlFeatureDto[] = response?.data ?? [];
  const meta = (response as any)?.meta;

  const onAdd = () => {
    setEditingId(null);
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingId(id);
    setOpenDropdownId(null);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingId(id);
    setOpenDropdownId(null);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    if (!deletingId) return;
    try {
      await deleteFeature(deletingId).unwrap();
      setDeletingId(null);
      setShowDeleteModal(false);
    } catch (e) {}
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page });
  };

  const formatSchedule = (f: ControlFeatureDto) => {
    const a = f.availability;
    if (!a) return t("allDay");

    if (a.daySchedules && a.daySchedules.length > 0) {
      return [...a.daySchedules]
        .sort((x, y) => x.dayOfWeek - y.dayOfWeek)
        .map((ds) => {
          const dayName = t(DAYS_KEYS[ds.dayOfWeek]);
          if (ds.allDay) return dayName + " " + t("allDay");
          const start = ds.startTime ? formatTime(ds.startTime) : "";
          const end = ds.endTime ? formatTime(ds.endTime) : "";
          return start && end ? `${dayName} ${start}–${end}` : dayName;
        })
        .join(", ");
    }

    if (a.allDay) {
      if (a.daysOfWeek && a.daysOfWeek.length > 0) {
        const dayNames = [...(a.daysOfWeek as number[])]
          .sort((x, y) => x - y)
          .map((d) => t(DAYS_KEYS[d]));
        return dayNames.join(", ");
      }
      return t("allDay");
    }
    const start = a.startTime || "";
    const end = a.endTime || "";
    if (start && end) {
      const dayPart =
        a.daysOfWeek && a.daysOfWeek.length > 0
          ? [...(a.daysOfWeek as number[])]
              .sort((x, y) => x - y)
              .map((d) => t(DAYS_KEYS[d]))
              .join(", ") + " — "
          : "";
      return `${dayPart}${formatTime(start)} / ${formatTime(end)}`;
    }
    return t("allDay");
  };

  const formatTime = (time: string) => {
    if (!time) return "";
    const [h, m] = time.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const h12 = hour % 12 || 12;
    return `${h12.toString().padStart(2, "0")}:${m} ${ampm}`;
  };

  document.title = `${t("documentTitle", { pageName: "features" })}`;

  const onToggleState = async (id: string) => {
    try {
      await toggleFeatureState(id).unwrap();
    } catch (err) {}
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("features")} pageTitle={t("features")} />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                  <h5 className="card-title mb-0">{t("features")}</h5>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t("loading")}</span>
                    </div>
                  </div>
                ) : isSuccess && features.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    {t("noRecordsFound")}
                  </div>
                ) : (
                  <Row className="g-3">
                    {features.map((item) => (
                      <Col
                        key={item.id}
                        xs={12}
                        sm={6}
                        lg={4}
                        xl={4}
                        className="d-flex"
                      >
                        <Card className="flex-grow-1 border shadow-none feature-card">
                          <CardBody className="d-flex flex-column">
                            <div className="d-flex align-items-start justify-content-between mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="rounded overflow-hidden bg-light d-flex align-items-center justify-content-center"
                                  style={{ width: 48, height: 48 }}
                                >
                                  {item.icon?.path ? (
                                    <img
                                      src={item.icon.path}
                                      alt=""
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <i className="ri-apps-2-line fs-4 text-muted" />
                                  )}
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="badge bg-soft-secondary text-body font-size-11">
                                      #{item.sequenceNumber ?? "—"}
                                    </span>
                                    <h6 className="mb-0 text-truncate flex-grow-1">
                                      {i18n.language === "ar"
                                        ? item.title.ar
                                        : item.title.en || "—"}
                                    </h6>
                                  </div>

                                  <small className="text-muted text-truncate d-block">
                                    {i18n.language === "ar"
                                      ? item.description?.ar
                                      : item.description?.en || "—"}
                                  </small>
                                  <Badge
                                    color={`${item.isActive ? "primary" : "danger"}`}
                                  >
                                    {item.isActive
                                      ? t("active")
                                      : t("inActive")}
                                  </Badge>
                                </div>
                              </div>
                              <Dropdown
                                isOpen={openDropdownId === item.id}
                                toggle={() =>
                                  setOpenDropdownId(
                                    openDropdownId === item.id ? null : item.id,
                                  )
                                }
                              >
                                <DropdownToggle
                                  tag="button"
                                  className="btn btn-soft-secondary btn-icon btn-sm rounded"
                                  caret={false}
                                >
                                  <i className="ri-more-2-fill" />
                                </DropdownToggle>
                                <DropdownMenu end>
                                  <DropdownItem onClick={() => onEdit(item.id)}>
                                    <i className="ri-pencil-line me-2" />
                                    {t("edit")}
                                  </DropdownItem>
                                  <DropdownItem
                                    style={{
                                      alignSelf: "flex-start",
                                      display: "flex",
                                    }}
                                    className="dropdown-item edit-list"
                                    onClick={() =>
                                      onToggleState && onToggleState(item.id)
                                    }
                                  >
                                    <i
                                      className={`${
                                        item.isActive
                                          ? "ri-forbid-line"
                                          : "ri-checkbox-circle-line text-danger"
                                      }  me-2 align-bottom text-muted`}
                                    ></i>
                                    {item.isActive
                                      ? t("deactivate")
                                      : t("activate")}
                                  </DropdownItem>
                                  {/* <DropdownItem
                                    className="text-danger"
                                    onClick={() => onDelete(item.id)}
                                  >
                                    <i className="ri-delete-bin-line me-2" />
                                    {t("delete")}
                                  </DropdownItem> */}
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                            <div className="mt-auto pt-2">
                              <small className="text-muted d-flex align-items-center">
                                <i className="ri-time-line me-1" />
                                {formatSchedule(item)}
                              </small>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                <DeleteModal
                  show={showDeleteModal}
                  loading={deleteLoading}
                  onCloseClick={() => {
                    setShowDeleteModal(false);
                    setDeletingId(null);
                  }}
                  onDeleteClick={onDeleteConfirm}
                />
                <AddEditFeatureModal
                  id={editingId}
                  show={showAddEditModal}
                  onCloseClick={() => {
                    setShowAddEditModal(false);
                    setEditingId(null);
                  }}
                />
                {!isLoading && isSuccess && meta && features.length > 0 && (
                  <Paginator
                    itemsOnCurrentPage={features.length}
                    itemsPerPage={meta.itemsPerPage || paginateQuery.limit}
                    totalItems={meta.totalItems || 0}
                    currentPage={meta.currentPage || paginateQuery.page}
                    totalPages={meta.totalPages || 0}
                    onChangePage={onChangePage}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const DAYS_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const FeaturesPage = withRouter(FeaturesPageComponent);
